import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from 'src/components/common/dropdown';
import { ALL_CLASSES } from 'src/constants/classes.constants';
import { useActiveUserClasses } from 'src/hooks/use-active-user-classes';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { SelectOption } from 'src/interfaces/select.types';
import eventService from 'src/services/event.service';
import {
  setHasViewUpdated,
  setSelectedClass,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { generateClassPickerOption } from 'src/utils/class-picker.utils';

import './class-picker.scss';

const ClassPicker = () => {
  const [initialClass, setInitialClass] = useState<SelectOption>();

  const { activeUserClasses = [], isLoading: isUserClassesLoading } =
    useActiveUserClasses();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const dispatch = useAppDispatch();
  const selectedClass = useSelectedClassSelector();

  const { t } = useTranslation();

  const allClassesTitle = t('All Classes');

  const dropdownOptions = activeUserClasses.map(generateClassPickerOption);
  const initialSelectedOption = initialClass ? [initialClass.value] : undefined;

  const selectClass = (
    classId: string,
    eventType?: string,
    isInitialSelect?: boolean,
  ) => {
    const areMultipleClassesSelected = classId === ALL_CLASSES;

    const prevSelectedClassKey =
      !!selectedClass && selectedClass.length > 1
        ? ALL_CLASSES
        : selectedClass?.[0]?.sourcedId;

    const hasClassChanged =
      !!prevSelectedClassKey && classId !== prevSelectedClassKey;

    if (!isInitialSelect && hasClassChanged) {
      // reset app view update status to prevent query to assignment-service with stale args
      dispatch(setHasViewUpdated(false));
    }

    // handle xAPI and selected class setting when All Classes option selected
    if (areMultipleClassesSelected && !isInitialSelect) {
      eventService.send(
        XApiEventKeys.selectedClassFilter,
        {
          result: {
            classFiltersSelected: ALL_CLASSES,
          },
        },
        {
          result: {
            isRightClick: eventType === 'contextmenu',
            ...commonXapiContextPayload,
          },
        },
      );

      if (eventType !== 'contextmenu') {
        dispatch(setSelectedClass(activeUserClasses));
      }

      return;
    }

    const selectedTeacherClass = activeUserClasses.find(
      (cl) => cl.sourcedId === classId,
    );

    if (selectedTeacherClass && !isInitialSelect) {
      eventService.send(
        XApiEventKeys.selectedClassFilter,
        {
          result: {
            classFiltersSelected: selectedTeacherClass.sourcedId,
          },
        },
        {
          result: {
            isRightClick: eventType === 'contextmenu',
            ...commonXapiContextPayload,
          },
        },
      );

      if (eventType !== 'contextmenu') {
        dispatch(setSelectedClass([selectedTeacherClass]));
      }
    }
  };

  const handleSelectClassDropdown = (
    classId: string,
    _: string,
    eventType?: string,
  ) => {
    selectClass(classId, eventType);
  };

  useEffect(() => {
    if (!selectedClass) return;

    if (selectedClass.length > 1) {
      setInitialClass({
        value: ALL_CLASSES,
        title: allClassesTitle,
      });
      return;
    }

    const preparedClass = generateClassPickerOption(selectedClass[0]);

    setInitialClass(preparedClass);
  }, [selectedClass, allClassesTitle]);

  if (!isUserClassesLoading && !activeUserClasses.length) return null;

  return (
    <Dropdown
      onSelect={handleSelectClassDropdown}
      initialSelectedId={initialSelectedOption}
      customDropdownText="Select a class"
      ariaLabel="Select a class"
      classNames={{
        container: 'top-banner__class-picker',
        button: 'top-banner__class-picker-button',
      }}
      testId="class-picker"
      isDisabled={dropdownOptions.length < 2}
      isLoading={isUserClassesLoading}
    >
      {activeUserClasses.length > 1 && (
        <Dropdown.Item id={ALL_CLASSES}>{t('All Classes')}</Dropdown.Item>
      )}
      {dropdownOptions.map((cl) => {
        return (
          <Dropdown.Item key={cl.value} id={cl.value}>
            {cl.title}
          </Dropdown.Item>
        );
      })}
    </Dropdown>
  );
};

export default ClassPicker;
